import { MASTER_ACCOUNT_BOARD_RETURN_URL_KEY } from '../constants/account-entry';

/** What is stored: the board URL and the master account whose board it belongs to. */
interface SavedMasterAccountBoardUrl {
  masterAccountId: string;
  url: string;
}

function isSavedMasterAccountBoardUrl(value: unknown): value is SavedMasterAccountBoardUrl {
  return (
    typeof value === 'object' &&
    value !== null &&
    'masterAccountId' in value &&
    'url' in value &&
    typeof value.masterAccountId === 'string' &&
    typeof value.url === 'string'
  );
}

/**
 * Saves the board URL (path + query) of `masterAccountId` being left. localStorage rather than
 * sessionStorage: opening a client in a second tab would otherwise lose the position.
 */
export function rememberMasterAccountBoardUrl(masterAccountId: string, url: string): void {
  try {
    const saved: SavedMasterAccountBoardUrl = { masterAccountId, url };
    localStorage.setItem(MASTER_ACCOUNT_BOARD_RETURN_URL_KEY, JSON.stringify(saved));
  } catch {
    // Storage is a convenience here — a blocked one only costs the saved position.
  }
}

/** The board URL saved for `masterAccountId` (the master account's `Account` Node ID), left in storage. */
export function peekMasterAccountBoardUrl(masterAccountId: string): string | null {
  try {
    const raw = localStorage.getItem(MASTER_ACCOUNT_BOARD_RETURN_URL_KEY);
    if (raw === null) return null;
    const saved: unknown = JSON.parse(raw);
    return isSavedMasterAccountBoardUrl(saved) && saved.masterAccountId === masterAccountId
      ? saved.url
      : null;
  } catch {
    return null;
  }
}

/** Reads the board URL saved for `masterAccountId` and forgets it; another master account's URL is dropped too. */
export function takeMasterAccountBoardUrl(masterAccountId: string): string | null {
  const url = peekMasterAccountBoardUrl(masterAccountId);
  forgetMasterAccountBoardUrl();
  return url;
}

/**
 * Drops the saved board URL. Signing out must call this: the URL outlives the session now that
 * it is in localStorage, and the next person on this browser must never inherit it.
 */
export function forgetMasterAccountBoardUrl(): void {
  try {
    localStorage.removeItem(MASTER_ACCOUNT_BOARD_RETURN_URL_KEY);
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}
